/**
 * The filter MODEL, separate from the filter PANEL.
 *
 * Both browse pages need to know what "cleared" means and how many facets the
 * user has narrowed, in places the panel itself is not rendered: an empty
 * state's "clear all filters" action, a filter sheet's footer, and the applied
 * count on the mobile filter button. Reaching into the panel component for that
 * would couple three surfaces to one component's module, which is also exactly
 * what breaks the moment a test mocks the panel.
 *
 * So it lives here. No JSX, no imports, nothing to mock.
 */

/**
 * The condition ladder, best to worst.
 *
 * ORDER IS THE DATA. It is not alphabetical and must never be sorted: a buyer
 * reads down this list until it stops being acceptable, so "Heavily Played"
 * next to "Lightly Played" would make the list unreadable. There are no PSA /
 * BGS / CGC grades in this product; condition is the only quality axis.
 *
 * It lives here rather than in a panel because both the rail and the filter
 * sheet render it, and neither owns it.
 */
export const CONDITIONS = [
  'Mint',
  'Near Mint',
  'Lightly Played',
  'Moderately Played',
  'Heavily Played',
];

/**
 * The cleared filter set. The same shape both pages initialise from.
 *
 * `priceRange` IS A PROBLEM AND IT IS LEFT ALONE ON PURPOSE. `Marketplace.jsx`
 * sends it as `min_price`/`max_price` on every request, so the marketplace asks
 * for nothing over R1,000 — and then renders cards at R21,500, which means the
 * ceiling is either ignored server-side or is quietly capping browse. Nothing
 * in the UI reveals which, because no surface has ever drawn a price control.
 * Changing the numbers here changes what the API is asked for on two pages, so
 * it is a product decision, not a design one. It is why the filter sheet ships
 * without the price facet the library drew.
 */
export const CLEARED_FILTERS = {
  category: 'All',
  rarity: [],
  condition: [],
  priceRange: [0, 1000],
  available_to_buy: true,
  catalog_group_id: '',
  set: '',
  productType: '',
  tcg_category_id: '',
};

/**
 * How many facets the user has narrowed.
 *
 * Drives the ink applied-count pill on the mobile filter button and the count
 * badges in the desktop rail's facet heads. It counts COMMANDS the user issued,
 * which is why `available_to_buy` only counts when it is turned OFF: it ships
 * on, so leaving it on is not a choice anyone made.
 */
export function countAppliedFilters(filters) {
  if (!filters) return 0;
  let n = 0;
  if (filters.available_to_buy === false) n += 1;
  if (filters.tcg_category_id) n += 1;
  if (filters.catalog_group_id || filters.set) n += 1;
  if (Array.isArray(filters.rarity)) n += filters.rarity.length;
  if (Array.isArray(filters.condition)) n += filters.condition.length;
  if (filters.productType) n += 1;
  return n;
}

/**
 * Choose a TCG, and drop what that choice invalidates.
 *
 * Both a RARITY and a SET belong to one game. "Holo Rare" does not exist in
 * One Piece and "Evolving Skies" is not a Lorcana set, so carrying either
 * across a game change hands the buyer a filter combination that can only ever
 * return zero results — and it does it silently, from a panel they are no
 * longer looking at.
 *
 * The rarity half of this rule was already in the desktop rail. It lives here
 * now because the filter sheet has to apply the identical rule, and a rule
 * about what a filter MEANS is model, not panel.
 */
export function selectTcgCategory(filters, tcgCategoryId) {
  return {
    ...filters,
    tcg_category_id: tcgCategoryId,
    rarity: [],
    catalog_group_id: '',
    set: '',
  };
}
